import React, { useEffect, useState } from 'react';
import { WarningIcon } from 'react-line-awesome';
import { useStyletron } from 'baseui';
import { Heading, HeadingLevel } from 'baseui/heading';
import { Spinner } from 'baseui/spinner';
import { listAssignments } from '@gtintel/platform-api';
import { RequestContext } from '../ProjectApp/context';
import { useFlux } from '../state/useFlux';
import { initialState, mutations } from './state';
const PAGE_LIMIT = 25;
const processListNPRequest = async (params, searchTerm, statuses, jurisdictions, opsLeads, filterBefore, filterAfter) => {
  const project_data = await listAssignments(params);
  const next = new URLSearchParams(String(project_data.next).split('?')[1]);
  const prev = new URLSearchParams(String(project_data.previous).split('?')[1]);
  const totalPages = Math.ceil(project_data.count / PAGE_LIMIT);
  let currentPage;
  if (project_data.next) {
    currentPage = Math.abs(Number(next.get('offset')) / PAGE_LIMIT);
  } else if (project_data.previous) {
    currentPage = Math.abs(Number(prev.get('offset'))) / PAGE_LIMIT + 2;
  } else {
    currentPage = 1;
  }
  const newState = {
    assignmentsByProject: project_data.results,
    searchTerm: searchTerm ?? '',
    page: currentPage,
    totalPages,
    prev: {
      search: searchTerm ?? '',
      limit: prev.get('limit'),
      offset: prev.get('offset')
    },
    next: {
      search: searchTerm ?? '',
      limit: next.get('limit'),
      offset: next.get('offset')
    },
    filterStatuses: statuses,
    filterJurisdictions: jurisdictions,
    filterOpsLeads: opsLeads,
    filterDateBefore: filterBefore,
    filterDateAfter: filterAfter
  };
  return newState;
};
const parseLocalStorageFilters = () => {
  const savedFilterStatuses = JSON.parse(localStorage.getItem('filterStatuses'));
  const savedFilterJurisdictions = JSON.parse(localStorage.getItem('filterJurisdictions'));
  const savedFilterOpsLeads = JSON.parse(localStorage.getItem('filterOpsLeads'));
  const savedSearchTerm = localStorage.getItem('searchTerm');
  const savedFilterBefore = localStorage.getItem('dateBefore') ? new Date(localStorage.getItem('dateBefore')) : null;
  const savedFilterAfter = localStorage.getItem('dateAfter') ? new Date(localStorage.getItem('dateAfter')) : null;
  return {
    savedFilterStatuses,
    savedFilterJurisdictions,
    savedFilterOpsLeads,
    savedSearchTerm,
    savedFilterAfter,
    savedFilterBefore
  };
};
export const RequestContextProvider = ({
  site,
  children
}) => {
  const [state, mutate] = useFlux(initialState());
  const [css] = useStyletron();
  const [isLoading, setIsLoading] = useState(true);
  const [requestFailed, setRequestFailed] = useState(false);
  const buildQueryParams = (savedFilterStatuses, savedFilterJurisdictions, savedFilterOpsLeads, savedSearchTerm, savedFilterAfter, savedFilterBefore, page) => {
    const filterStatuses = [];
    const filterJurisdictions = [];
    const filterOpsLeads = [];
    const paramsArray = [];
    if (savedFilterStatuses) {
      savedFilterStatuses.map(s => paramsArray.push(['status', s.id]));
      filterStatuses.push(...savedFilterStatuses);
    } else {
      state?.filterStatuses && state.filterStatuses.map(s => paramsArray.push(['status', s.id]));
      state?.filterStatuses && filterStatuses.push(...state.filterStatuses);
    }
    if (savedFilterJurisdictions) {
      savedFilterJurisdictions.map(s => paramsArray.push(['jurisdiction', s.id]));
      filterJurisdictions.push(...savedFilterJurisdictions);
    } else {
      state?.filterJurisdictions && state.filterJurisdictions.map(s => paramsArray.push(['jurisdiction', s.id]));
      state?.filterJurisdictions && filterJurisdictions.push(...state.filterJurisdictions);
    }
    if (savedFilterOpsLeads) {
      savedFilterOpsLeads.map(s => paramsArray.push(['ops_lead', s.id]));
      filterOpsLeads.push(...savedFilterOpsLeads);
    } else {
      state?.filterOpsLeads && state.filterOpsLeads.map(s => paramsArray.push(['ops_lead', s.id]));
      state?.filterOpsLeads && filterOpsLeads.push(...state.filterOpsLeads);
    }
    if (savedFilterBefore) {
      paramsArray.push(['updated_date_before', savedFilterBefore.toISOString()]);
    } else {
      state?.filterDateBefore && paramsArray.push(['updated_date_before', state.filterDateBefore.toISOString()]);
    }
    if (savedFilterAfter) {
      paramsArray.push(['updated_date_after', savedFilterAfter.toISOString()]);
    } else {
      state?.filterDateAfter && paramsArray.push(['updated_date_after', state.filterDateAfter.toISOString()]);
    }
    if (page) {
      paramsArray.push(...Object.entries(page));
    } else {
      paramsArray.push(['limit', `${PAGE_LIMIT}`]);
    }
    savedSearchTerm && paramsArray.push(['name', savedSearchTerm]);
    return {
      params: new URLSearchParams(paramsArray),
      filterStatuses,
      filterJurisdictions,
      filterOpsLeads
    };
  };
  const getAssignments = page => {
    const {
      savedFilterStatuses,
      savedFilterJurisdictions,
      savedFilterOpsLeads,
      savedSearchTerm,
      savedFilterAfter,
      savedFilterBefore
    } = parseLocalStorageFilters();
    const {
      params,
      filterStatuses,
      filterJurisdictions,
      filterOpsLeads
    } = buildQueryParams(savedFilterStatuses, savedFilterJurisdictions, savedFilterOpsLeads, savedSearchTerm, savedFilterAfter, savedFilterBefore, page);
    processListNPRequest(params.toString(), savedSearchTerm, filterStatuses, filterJurisdictions, filterOpsLeads, savedFilterBefore, savedFilterAfter).then(newState => {
      mutate(mutations.init({
        state: newState
      }));
      setIsLoading(false);
      setRequestFailed(false);
    }).catch(e => {
      setIsLoading(false);
      setRequestFailed(true);
      console.error(e);
    });
  };
  useEffect(() => {
    getAssignments();
  }, []);
  const ctx = {
    listProjects: () => state.assignmentsByProject,
    site: () => site,
    searchTerm: () => state.searchTerm,
    editSearchTerm: term => {
      mutate(mutations.editSearchTerm({
        term
      }));
    },
    filterStatuses: () => state.filterStatuses,
    filterJurisdictions: () => state.filterJurisdictions,
    filterOpsLeads: () => state.filterOpsLeads,
    setFilterStatuses: statuses => {
      mutate(mutations.setFilterStatuses({
        statuses
      }));
    },
    setFilterJurisdictions: jurisdictions => {
      mutate(mutations.setFilterJurisdictions({
        jurisdictions
      }));
    },
    filterDateBefore: () => state.filterDateBefore,
    filterDateAfter: () => state.filterDateAfter,
    setFilterDateBefore: date => {
      mutate(mutations.setFilterDateBefore({
        date
      }));
    },
    clearFilterDateBefore: () => mutate(mutations.clearFilterDateBefore()),
    setFilterDateAfter: date => {
      mutate(mutations.setFilterDateAfter({
        date
      }));
    },
    clearFilterDateAfter: () => mutate(mutations.clearFilterDateAfter()),
    filter: () => {
      getAssignments();
    },
    currentPage: () => state.page,
    setCurrentPage: page => {
      mutate(mutations.setCurrentPage({
        page
      }));
    },
    totalPages: () => state.totalPages,
    search: () => {
      getAssignments();
    },
    getNext: page => {
      mutate(mutations.setCurrentPage({
        page: page < state.totalPages ? page : state.totalPages
      }));
      getAssignments({
        offset: String((page - 1) * PAGE_LIMIT),
        limit: `${PAGE_LIMIT}`
      });
    },
    getPrev: page => {
      mutate(mutations.setCurrentPage({
        page: page > 0 ? page : 0
      }));
      getAssignments({
        offset: String((page - 1) * PAGE_LIMIT > 0 ? (page - 1) * PAGE_LIMIT : 0),
        limit: `${PAGE_LIMIT}`
      });
    }
  };
  if (isLoading) {
    return <section className={css({
      alignItems: 'center',
      display: 'flex',
      height: 'calc(100vh - 166px)',
      justifyContent: 'center',
      width: 'calc(100vw - 80px)'
    })}>
        <Spinner />
      </section>;
  }
  if (requestFailed) {
    return <section className={css({
      alignItems: 'center',
      display: 'flex',
      flexFlow: 'column nowrap',
      height: 'calc(100vh - 166px)',
      justifyContent: 'center',
      width: 'calc(100vw - 80px)'
    })}>
        <div className={css({
        display: 'flex',
        gap: '12px'
      })}>
          <WarningIcon className={css({
          color: 'black !important',
          fontSize: '52px'
        })} />
          <HeadingLevel>
            <Heading>Request Failed</Heading>
          </HeadingLevel>
        </div>

        <p>
          Wait a second and refresh the page. If the problem persists, please
          contact GTI.
        </p>
      </section>;
  }
  return <RequestContext.Provider value={ctx}>{children}</RequestContext.Provider>;
};